import { useCallback, useMemo, useState, type ReactElement } from "react";
import { Play, Plus } from "lucide-react";
import { space } from "@tokens/index";
import {
    Badge,
    Button,
    ConfirmDialog,
    SkeletonRows,
    Stack,
    Text,
    TextInput,
} from "@primitives/index";
import {
    AdminPage,
    AdminPageHeader,
    AdminSection,
    AdminSelect,
    EmptyLine,
    ErrorAlert,
    Field,
    FormActions,
    MutationError,
} from "./admin-ui";
import { useMyQueues } from "./queues";
import {
    PredicateEditor,
    RuleEditor,
    RuleRow,
    emptyForm,
    fromRule,
    toPayload,
    useCreateRoutingRule,
    useDeleteRoutingRule,
    useRoutingRules,
    useRoutingTagKeys,
    useSetRoutingRuleEnabled,
    useTestRouting,
    useUpdateRoutingRule,
} from "./routing";
import type { PredicatePair, RoutingRule, RuleFormState } from "./routing";
import { isRuleRoutableKind, toTaskTypeOptions, useTaskTypes, useWorkflowKinds } from "./workflows";
import type { TaskTypeOption } from "./workflows";

// ---------------------------------------------------------------------------
// Dry-run preview panel
// ---------------------------------------------------------------------------

interface PreviewState {
    readonly workflowKind: string;
    readonly entityType: string;
    readonly taskType: string;
    readonly tags: PredicatePair[];
}

function emptyPreview(): PreviewState {
    return { workflowKind: "", entityType: "", taskType: "", tags: [] };
}

interface DryRunPanelProps {
    readonly availableKinds: readonly string[];
    readonly availableTaskTypes: readonly TaskTypeOption[];
    readonly availableTagKeys: readonly string[];
    readonly queueNamesById: ReadonlyMap<string, string>;
    readonly rulesById: ReadonlyMap<string, RoutingRule>;
}

function DryRunPanel({
    availableKinds,
    availableTaskTypes,
    availableTagKeys,
    queueNamesById,
    rulesById,
}: DryRunPanelProps): ReactElement {
    const [state, setState] = useState<PreviewState>(emptyPreview);
    const testMutation = useTestRouting();

    const handleRun = useCallback(() => {
        const tags: Record<string, string> = {};
        for (const t of state.tags) {
            if (t.key.length === 0 || t.value.length === 0) continue;
            tags[t.key] = t.value;
        }
        testMutation.mutate({
            workflowKind: state.workflowKind.trim(),
            entityType: state.entityType.trim(),
            taskType: state.taskType.length > 0 ? state.taskType : null,
            routingTags: tags,
        });
    }, [state, testMutation]);

    const canRun =
        state.workflowKind.trim().length > 0 &&
        state.entityType.trim().length > 0 &&
        !testMutation.isPending;

    const result = testMutation.data;

    return (
        <AdminSection
            title="Test routing"
            description="Run a hypothetical work item through the live router. Nothing is persisted."
        >
            <Stack direction="column" gap={4}>
                <Field label="Workflow kind">
                    {(id) => (
                        <AdminSelect
                            id={id}
                            value={state.workflowKind}
                            onChange={(e) =>
                                setState((s) => ({ ...s, workflowKind: e.target.value }))
                            }
                        >
                            <option value="" disabled>
                                {availableKinds.length === 0
                                    ? "Loading workflow kinds…"
                                    : "Select a workflow kind"}
                            </option>
                            {availableKinds.map((k) => (
                                <option key={k} value={k}>
                                    {k}
                                </option>
                            ))}
                        </AdminSelect>
                    )}
                </Field>

                <Field label="Entity type">
                    {(id) => (
                        <TextInput
                            id={id}
                            fullWidth
                            placeholder="e.g. order"
                            value={state.entityType}
                            onChange={(e) =>
                                setState((s) => ({ ...s, entityType: e.target.value }))
                            }
                        />
                    )}
                </Field>

                <Field label="Task type (optional)">
                    {(id) => (
                        <AdminSelect
                            id={id}
                            value={state.taskType}
                            onChange={(e) =>
                                setState((s) => ({ ...s, taskType: e.target.value }))
                            }
                        >
                            <option value="">No task type (message / note)</option>
                            {availableTaskTypes.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.label}
                                </option>
                            ))}
                        </AdminSelect>
                    )}
                </Field>

                <div style={{ display: "grid", gap: space[1], minWidth: 0 }}>
                    <Text as="p" role="bodySm" weight="medium" tone="muted">
                        Routing tags
                    </Text>
                    <PredicateEditor
                        predicates={state.tags}
                        availableKeys={availableTagKeys}
                        onChange={(next) => setState((s) => ({ ...s, tags: next }))}
                    />
                </div>

                <FormActions
                    primary={
                        <Button
                            type="button"
                            variant="primary"
                            size="md"
                            onClick={handleRun}
                            disabled={!canRun}
                            busy={testMutation.isPending}
                        >
                            <Play size={14} strokeWidth={2.25} aria-hidden="true" />
                            Run preview
                        </Button>
                    }
                />

                <MutationError error={testMutation.error} />

                {result !== undefined && (
                    <Stack direction="column" gap={3}>
                        <Stack direction="column" gap={1}>
                            <Text role="caption" tone="muted">
                                Result
                            </Text>
                            <div style={{ display: "flex", gap: space[2], flexWrap: "wrap" }}>
                                <Badge
                                    variant={
                                        result.primaryQueueId !== null
                                            ? result.usedTriage
                                                ? "warning"
                                                : "success"
                                            : "danger"
                                    }
                                    size="sm"
                                >
                                    {result.usedTriage
                                        ? "TRIAGE FALLBACK"
                                        : result.primaryQueueId !== null
                                            ? "ROUTED"
                                            : "UNROUTED"}
                                </Badge>
                            </div>
                        </Stack>

                        <Stack direction="column" gap={1}>
                            <Text role="caption" tone="muted">
                                Resulting queue
                            </Text>
                            {result.primaryQueueId === null ? (
                                <Text role="caption" tone="muted">
                                    None — work item would be left unrouted.
                                </Text>
                            ) : (
                                <div style={{ display: "flex", gap: space[2], flexWrap: "wrap" }}>
                                    <Badge variant="neutral" size="sm" caps={false}>
                                        {queueNamesById.get(result.primaryQueueId) ??
                                            result.primaryQueueId}
                                    </Badge>
                                </div>
                            )}
                        </Stack>

                        <Stack direction="column" gap={1}>
                            <Text role="caption" tone="muted">
                                Matched rules ({result.matchedRuleIds.length})
                            </Text>
                            {result.matchedRuleIds.length === 0 ? (
                                <Text role="caption" tone="muted">
                                    No rules matched.
                                </Text>
                            ) : (
                                <Stack direction="column" gap={1}>
                                    {result.matchedRuleIds.map((id) => {
                                        const r = rulesById.get(id);
                                        return (
                                            <Text key={id} role="bodySm" truncate>
                                                {r?.description ?? id}
                                            </Text>
                                        );
                                    })}
                                </Stack>
                            )}
                        </Stack>
                    </Stack>
                )}
            </Stack>
        </AdminSection>
    );
}

// ---------------------------------------------------------------------------
// Page entry
// ---------------------------------------------------------------------------

type EditorState =
    | { readonly mode: "idle" }
    | { readonly mode: "create"; readonly form: RuleFormState }
    | { readonly mode: "edit"; readonly ruleId: string; readonly form: RuleFormState };

function RoutingAdmin(): ReactElement {
    const rulesQuery = useRoutingRules();
    const tagKeysQuery = useRoutingTagKeys();
    const kindsQuery = useWorkflowKinds();
    const taskTypesQuery = useTaskTypes();
    const queuesQuery = useMyQueues();

    const createMutation = useCreateRoutingRule();
    const updateMutation = useUpdateRoutingRule();
    const enableMutation = useSetRoutingRuleEnabled();
    const deleteMutation = useDeleteRoutingRule();

    const [editor, setEditor] = useState<EditorState>({ mode: "idle" });
    const [pendingDelete, setPendingDelete] = useState<RoutingRule | null>(null);

    // Memoized data slices — protect downstream useMemo deps from
    // recreating identity on every render when the queries are pending
    // and the `?? []` short-circuit would otherwise hand back a fresh
    // array reference each pass.
    const rules = useMemo(() => rulesQuery.data?.items ?? [], [rulesQuery.data]);
    const queues = useMemo(() => queuesQuery.data?.items ?? [], [queuesQuery.data]);
    const availableTagKeys = useMemo(
        () => tagKeysQuery.data?.keys ?? [],
        [tagKeysQuery.data],
    );
    const availableKinds = useMemo(
        () => (kindsQuery.data?.kinds ?? []).filter(isRuleRoutableKind),
        [kindsQuery.data],
    );
    const availableTaskTypes = useMemo(
        () => toTaskTypeOptions(taskTypesQuery.data?.taskTypes ?? []),
        [taskTypesQuery.data],
    );

    const queueNamesById = useMemo(() => {
        const map = new Map<string, string>();
        for (const q of queues) {
            map.set(q.id, q.name);
        }
        return map;
    }, [queues]);

    const rulesById = useMemo(() => {
        const map = new Map<string, RoutingRule>();
        for (const r of rules) {
            map.set(r.id, r);
        }
        return map;
    }, [rules]);

    const beginCreate = useCallback(() => {
        setEditor({ mode: "create", form: emptyForm() });
    }, []);

    const beginEdit = useCallback((rule: RoutingRule) => {
        setEditor({ mode: "edit", ruleId: rule.id, form: fromRule(rule) });
    }, []);

    const cancelEdit = useCallback(() => {
        setEditor({ mode: "idle" });
        createMutation.reset();
        updateMutation.reset();
    }, [createMutation, updateMutation]);

    const handleSubmit = useCallback(() => {
        if (editor.mode === "idle") return;
        const payload = toPayload(editor.form);
        if (editor.mode === "create") {
            createMutation.mutate(
                {
                    workflowKind: payload.workflowKind,
                    entityType: payload.entityType,
                    taskType: payload.taskType,
                    description: payload.description,
                    tagPredicates: payload.tagPredicates,
                    targetQueueId: payload.targetQueueId,
                },
                {
                    onSuccess: () => setEditor({ mode: "idle" }),
                },
            );
        } else {
            updateMutation.mutate(
                {
                    id: editor.ruleId,
                    entityType: payload.entityType,
                    taskType: payload.taskType,
                    description: payload.description,
                    tagPredicates: payload.tagPredicates,
                    targetQueueId: payload.targetQueueId,
                },
                {
                    onSuccess: () => setEditor({ mode: "idle" }),
                },
            );
        }
    }, [editor, createMutation, updateMutation]);

    const handleToggle = useCallback(
        (rule: RoutingRule) => {
            enableMutation.mutate({ id: rule.id, enabled: !rule.isEnabled });
        },
        [enableMutation],
    );

    const confirmDelete = useCallback(() => {
        if (pendingDelete === null) return;
        deleteMutation.mutate(pendingDelete.id);
        setPendingDelete(null);
    }, [deleteMutation, pendingDelete]);

    const newRuleButton = (
        <Button variant="primary" size="md" onClick={beginCreate}>
            <Plus size={14} strokeWidth={2.25} aria-hidden="true" />
            New rule
        </Button>
    );

    return (
        <>
            <AdminPageHeader
                title="Routing rules"
                description="Define how incoming work items are dispatched to queues based on routing tags."
                actions={editor.mode === "idle" ? newRuleButton : undefined}
            />

            {editor.mode !== "idle" && (
                <RuleEditor
                    mode={editor.mode}
                    form={editor.form}
                    onChange={(next) => {
                        setEditor((s) =>
                            s.mode === "idle" ? s : { ...s, form: next },
                        );
                    }}
                    availableKinds={availableKinds}
                    availableTaskTypes={availableTaskTypes}
                    availableTagKeys={availableTagKeys}
                    availableQueues={queues}
                    isSubmitting={createMutation.isPending || updateMutation.isPending}
                    submitError={
                        editor.mode === "create"
                            ? createMutation.error
                            : updateMutation.error
                    }
                    onSubmit={handleSubmit}
                    onCancel={cancelEdit}
                />
            )}

            <section aria-label="Rules" style={{ display: "grid", gap: space[3], minWidth: 0 }}>
                <Text as="h2" role="subheading">
                    Rules ({rules.length})
                </Text>
                {rulesQuery.isLoading && (
                    <SkeletonRows rows={3} rowHeight={96} shape="card" label="Loading routing rules" />
                )}
                {rulesQuery.error !== null && (
                    <ErrorAlert onRetry={() => void rulesQuery.refetch()}>
                        Couldn&rsquo;t load rules. {rulesQuery.error.message}
                    </ErrorAlert>
                )}
                {!rulesQuery.isLoading && rulesQuery.error === null && rules.length === 0 && (
                    <EmptyLine action={editor.mode === "idle" ? newRuleButton : undefined}>
                        No routing rules yet. Create one to start dispatching work.
                    </EmptyLine>
                )}
                <MutationError error={enableMutation.error ?? deleteMutation.error} />
                {rules.map((r) => {
                    const isToggling =
                        enableMutation.isPending && enableMutation.variables?.id === r.id;
                    const isDeleting =
                        deleteMutation.isPending && deleteMutation.variables === r.id;
                    return (
                        <RuleRow
                            key={r.id}
                            rule={r}
                            queueNamesById={queueNamesById}
                            isToggling={isToggling}
                            isDeleting={isDeleting}
                            onEdit={() => beginEdit(r)}
                            onToggleEnabled={() => handleToggle(r)}
                            onDelete={() => setPendingDelete(r)}
                        />
                    );
                })}
            </section>

            <DryRunPanel
                availableKinds={availableKinds}
                availableTaskTypes={availableTaskTypes}
                availableTagKeys={availableTagKeys}
                queueNamesById={queueNamesById}
                rulesById={rulesById}
            />

            <ConfirmDialog
                open={pendingDelete !== null}
                title="Delete rule"
                message={`Delete rule "${pendingDelete?.description ?? ""}"? This cannot be undone.`}
                confirmLabel="Delete"
                tone="danger"
                onConfirm={confirmDelete}
                onCancel={() => setPendingDelete(null)}
            />
        </>
    );
}

export function RoutingRulesAdminPage(): ReactElement {
    return (
        <AdminPage>
            <RoutingAdmin />
        </AdminPage>
    );
}
